import React, {useEffect, useMemo, useRef, useState} from 'react';
import {getIdTokenResult, inMemoryPersistence, setPersistence, signInWithCustomToken, signOut} from 'firebase/auth';
import {auth} from '../firebase';
import {motionReviewApi} from './api';
import MotionReviewScreen from './MotionReviewScreen';
import MotionHighlights from './MotionHighlights';
import {getMotionReviewOutbox} from './motionReviewOutbox';
import {isNativeStandby, parseNativeSession, parseNativeVisibility, postNative} from './citizenScienceBridge';
import {analyticsErrorArea, setCommunityAnalyticsActive, startCommunityAnalytics, trackCommunity, useCommunityPageView} from './analytics';
import CitizenScienceOnboarding from './CitizenScienceOnboarding';
import './community.css';
import './citizenScience.css';

const makeNonce = () => {
  if (window.crypto.randomUUID) return window.crypto.randomUUID().replace(/-/g, '');
  // Older supported iOS WebViews have getRandomValues but not randomUUID.
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
};

export function CitizenScienceGame({api, nonce, active = true}) {
  const [view, setView] = useState('review');
  useCommunityPageView(view, 'latest_sightings_app', active);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const receive = event => {
      try {
        if (typeof event.data !== 'string' || event.data.length > 1000) return;
        const message = JSON.parse(event.data);
        if (active && !busy && message.version === 1 && message.type === 'cs-view' && message.nonce === nonce && ['review', 'highlights'].includes(message.view)) {
          setView(message.view); window.scrollTo?.({top: 0});
        }
      } catch (_) { /* Ignore messages outside the game navigation protocol. */ }
    };
    window.addEventListener('message', receive); document.addEventListener('message', receive);
    postNative('cs-navigation', nonce, {view, busy});
    return () => { window.removeEventListener('message', receive); document.removeEventListener('message', receive); };
  }, [active, busy, nonce, view]);
  const [state, setState] = useState(null);
  const scope = typeof api.scope === 'function' ? api.scope() : api.scope;
  const outbox = useMemo(() => getMotionReviewOutbox({scope, api, isActive: () => !api.isActive || api.isActive(scope)}), [api, scope]);
  useEffect(() => outbox.subscribe(next => {
    setState(next);
    if (typeof next.reviewedCount === 'number') {
      postNative('cs-progress', nonce, {saved: next.reviewedCount, pending: next.pendingReviewedCount ?? next.pendingCount});
    }
  }), [outbox, nonce]);
  const reviewed = state && typeof state.reviewedCount === 'number'
    ? Math.max(0, state.reviewedCount + (state.pendingReviewedCount ?? state.pendingCount) + (state.countDelta || 0)) : undefined;
  return <div className="community-app cs-game">
    <div hidden={view !== 'review'}>
      <MotionReviewScreen api={api} active={active && view === 'review'} showLeaderboard reviewedCount={reviewed} onBusyChange={setBusy} />
    </div>
    {active && view === 'highlights' && <MotionHighlights api={api} />}
  </div>;
}

export default function CitizenScienceApp() {
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [visible, setVisible] = useState(() => !isNativeStandby());
  const visibleRef = useRef(visible);
  const loginRecorded = useRef(false);
  const nonce = useRef(null);
  if (!nonce.current) nonce.current = makeNonce();
  const nativeAvailable = Boolean(window.ReactNativeWebView?.postMessage);
  useEffect(() => {
    document.title = 'App | What’s hiding here? | Latest Sightings';
    if (!nativeAvailable) return undefined;
    // Use the website's tracker and event definitions for the embedded game.
    // Its startup guard prevents duplicate session events in React StrictMode.
    if (visibleRef.current) startCommunityAnalytics('latest_sightings_app');
    setCommunityAnalyticsActive(visibleRef.current);
    let active = true;
    let connecting = false;
    let signedIn = false;
    let failed = false;
    let handshakeTimer;
    // Remove any old auth persistence before accepting a new native identity.
    const prepared = setPersistence(auth, inMemoryPersistence).then(() => signOut(auth));
    const receive = async event => {
      const visibility = parseNativeVisibility(event.data, nonce.current);
      if (visibility && active) {
        visibleRef.current = visibility.active;
        // Initialize before rendering the screen so its first page view is kept.
        if (visibility.active) startCommunityAnalytics('latest_sightings_app');
        setCommunityAnalyticsActive(visibility.active);
        setVisible(visibility.active);
        return;
      }
      const value = parseNativeSession(event.data, nonce.current);
      if (!value || !active || connecting || signedIn || failed) return;
      connecting = true;
      try {
        await prepared;
        if (!active) return;
        const result = await signInWithCustomToken(auth, value.customToken);
        if (result.user.uid !== value.reviewerUid) throw new Error('Account mismatch');
        // This reads the freshly issued token already returned by sign-in.
        // The server checks completion for this account during the handoff.
        const {claims} = await getIdTokenResult(result.user);
        if (!active) { await signOut(auth); return; }
        signedIn = true;
        clearInterval(handshakeTimer);
        setSession({uid: result.user.uid, displayName: String(value.displayName || 'Wildlife helper').slice(0, 40),
          tutorialCompleted: claims.citizenScienceTutorialCompleted === true,
          introductionSkipped: claims.citizenScienceIntroductionSkipped === true,
          tutorialStarted: claims.citizenScienceTutorialStarted === true,
          introductionStateKnown: typeof claims.citizenScienceTutorialStarted === 'boolean'});
        postNative('cs-signed-in', nonce.current);
      } catch (requestError) {
        await signOut(auth).catch(() => {});
        if (active) {
          failed = true;
          clearInterval(handshakeTimer);
          if (visibleRef.current) trackCommunity('community_auth_error', {auth_action: 'app_handoff', error_code: analyticsErrorArea(requestError, 'auth_error')});
          setError('We couldn’t connect your account. Return to the app and try again.');
          postNative('cs-error', nonce.current);
        }
      } finally { connecting = false; }
    };
    window.addEventListener('message', receive);
    document.addEventListener('message', receive);
    const request = () => { if (active && !signedIn && !connecting && !failed) postNative('cs-ready', nonce.current); };
    prepared.then(request).catch(() => {
      if (active) {
        failed = true;
        clearInterval(handshakeTimer);
        setError('Your game session could not start. Please try again.');
        postNative('cs-error', nonce.current);
      }
    });
    handshakeTimer = setInterval(request, 2500);
    return () => {
      active = false;
      clearInterval(handshakeTimer);
      window.removeEventListener('message', receive);
      document.removeEventListener('message', receive);
      // Auth stays in memory. Pending answers remain in the existing UID-scoped
      // durable outbox and can only resume with that same account.
    };
  }, [nativeAvailable]);
  useEffect(() => {
    if (session && visible && !loginRecorded.current) {
      loginRecorded.current = true;
      trackCommunity('login', {method: 'latest_sightings'});
    }
  }, [session, visible]);
  const api = useMemo(() => session ? {
    ...motionReviewApi,
    leaderboard: async () => {
      const result = await motionReviewApi.leaderboard();
      return {...result, entries: result.entries.map(entry => entry.isYou ? {...entry, name: session.displayName} : entry)};
    },
  } : null, [session]);
  if (!nativeAvailable) return <main className="cr-empty cs-open-app"><h1>Citizen Science Game</h1><p>Open this game from the Latest Sightings app to play with your existing account.</p></main>;
  if (error) return <main className="cr-empty"><h1>We couldn’t start the game</h1><p>{error}</p></main>;
  if (!session) return <main className="cr-empty" role="status">Connecting your Latest Sightings account…</main>;
  return <CitizenScienceOnboarding key={session.uid} session={session} active={visible}>
    <CitizenScienceGame api={api} nonce={nonce.current} active={visible} />
  </CitizenScienceOnboarding>;
}
